// packages/ui/src/components/chat/useChatDraft.ts
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The text of a chat's message box, kept by the widget around a ChatComposer
 * (`<ChatComposer {...draft} />`). A message that did not go through comes back
 * into the box, so sending it again is the retry. It leaves the box again once it
 * got through after all, so it is never posted twice: asked again from elsewhere
 * on the page and delivered, or found stored by the widget's poll (`recovered`:
 * its answer was lost). Only a text this hook put back leaves; one the visitor
 * typed, or changed meanwhile, stays.
 *
 * @param send      sends a message (trimmed); resolves whether it went through.
 * @param recovered the last message reported as failed that the server had stored
 *                  after all; a new object each time.
 */
export function useChatDraft(send: (text: string) => Promise<boolean>, recovered: { text: string } | null) {
  const [value, setValue] = useState('');
  /** The failed text last put back into the box. */
  const restoredRef = useRef<string | null>(null);

  const withdraw = useCallback((text: string) => {
    if (restoredRef.current !== text) return;
    restoredRef.current = null;
    setValue((current) => (current.trim() === text ? '' : current));
  }, []);

  const onSend = useCallback(
    (text: string): Promise<boolean> => {
      const message = text.trim();
      const sent = send(message);
      void sent.then((delivered) => {
        if (delivered) {
          withdraw(message);
          return;
        }
        restoredRef.current = message;
        setValue((current) => (current.trim() === '' ? message : current));
      });
      return sent;
    },
    [send, withdraw],
  );

  useEffect(() => {
    if (recovered) withdraw(recovered.text);
  }, [recovered, withdraw]);

  return { value, onValueChange: setValue, onSend };
}
